// Sign-in (design/screens/signin.html, left card): credentials, the generic failure, the account lockout with its expiry, the
// per-IP throttle, the D21 organisation choice, and the install panel. The lock card of the prototype is the Lock screen.
import type { Ulid } from "@visionx/contracts";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { auth } from "../api/endpoints";
import { describeError, isApiError } from "../api/problem";
import { formatTime } from "../lib/format";
import { loadSession } from "../session/store";
import { Alert, Checkbox, Field, TextInput } from "../ui/bits";
import { Icon, Mark } from "../ui/icons";

interface OrgChoice { orgId: string; legalName: string; tradingName: string | null }

const EMAIL_KEY = "vx.email";
const readRemembered = (): string => { try { return localStorage.getItem(EMAIL_KEY) ?? ""; } catch { return ""; } };
const writeRemembered = (email: string | null): void => { try { if (email) localStorage.setItem(EMAIL_KEY, email); else localStorage.removeItem(EMAIL_KEY); } catch { /* storage unavailable */ } };

type InstallPrompt = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };
let deferredInstall: InstallPrompt | null = null;
if (typeof window !== "undefined") window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredInstall = e as InstallPrompt; });

const deviceKind = (): "Desktop" | "Tablet" => (typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches ? "Tablet" : "Desktop");

export function SignIn({ orgName }: { orgName?: string | undefined }) {
  const [email, setEmail] = useState(readRemembered);
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(() => readRemembered() !== "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [organisations, setOrganisations] = useState<OrgChoice[] | null>(null);
  const [installed, setInstalled] = useState(() => typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches);
  const [canInstall, setCanInstall] = useState(() => deferredInstall !== null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const onPrompt = () => setCanInstall(true);
    const onInstalled = () => { setInstalled(true); setCanInstall(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);
  useEffect(() => {
    if (!lockedUntil && retryAfter === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [lockedUntil, retryAfter]);

  const stillLocked = lockedUntil !== null && Date.parse(lockedUntil) > now;

  const submit = async (e: FormEvent, orgId?: string) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await auth.login({ email, password, deviceKind: deviceKind(), ...(orgId ? { orgId: orgId as Ulid } : {}) });
      if (data.requiresOrganisationChoice && data.organisations) { setOrganisations(data.organisations); return; }
      writeRemembered(remember ? email : null);
      await loadSession({ credentialAccepted: true });
    } catch (err) {
      if (isApiError(err) && err.code === "ACCOUNT_LOCKED") {
        setLockedUntil(typeof err.meta.lockedUntil === "string" ? err.meta.lockedUntil : new Date(Date.now() + 15 * 60_000).toISOString());
      } else if (isApiError(err) && err.code === "RATE_LIMITED") {
        setRetryAfter(typeof err.meta.retryAfterSeconds === "number" ? err.meta.retryAfterSeconds : 60);
        setTimeout(() => setRetryAfter(null), (typeof err.meta.retryAfterSeconds === "number" ? err.meta.retryAfterSeconds : 60) * 1000);
      } else if (isApiError(err) && err.code === "AUTHENTICATION_FAILED") {
        setError("Email or password not accepted.");
      } else {
        setError(describeError(err));
      }
      setPassword("");
    } finally {
      setBusy(false);
    }
  };

  const install = async () => {
    if (!deferredInstall) return;
    await deferredInstall.prompt();
    const choice = await deferredInstall.userChoice;
    if (choice.outcome === "accepted") setInstalled(true);
    deferredInstall = null;
    setCanInstall(false);
  };

  return (
    <section className="screen" id="s-signin" data-bare="true">
      <div className="content">
        <div className="wrap">
          <div className="cards">
            <div className="stack card">
              <div className="panel">
                <form className="panel-body" style={{ padding: 24, gap: 16 }} onSubmit={(e) => void submit(e)}>
                  <div className="logo"><Mark className="vxmark" /><span>Vision X</span></div>
                  <div className="stack gap-4">
                    <h1>{orgName ? `Sign in to ${orgName}` : "Sign in"}</h1>
                    <div className="help">Use your practice email address. Your branch is chosen after you sign in.</div>
                  </div>
                  {organisations ? (
                    <OrganisationChoice organisations={organisations} busy={busy} onChoose={(orgId) => void submit(new Event("submit") as unknown as FormEvent, orgId)} onBack={() => setOrganisations(null)} />
                  ) : (
                    <>
                      {stillLocked && lockedUntil && (
                        <Alert tone="crit"><b>Account locked.</b> Too many failed attempts. Try again after {formatTime(lockedUntil)}, or reset your password.</Alert>
                      )}
                      {retryAfter !== null && (
                        <Alert tone="warn"><b>Too many attempts from this device.</b> Wait {retryAfter} seconds and try again.</Alert>
                      )}
                      {error && !stillLocked && <Alert tone="crit">{error}</Alert>}
                      <Field label="Email" htmlFor="signin-email">
                        <TextInput id="signin-email" type="email" autoComplete="username" required value={email} disabled={busy || stillLocked} onChange={(e) => setEmail(e.target.value)} autoFocus={email === ""} />
                      </Field>
                      <Field label="Password" htmlFor="signin-password">
                        <div className={`input${busy || stillLocked ? " disabled" : ""}`}>
                          <input id="signin-password" className={show ? "" : "dots"} type={show ? "text" : "password"} autoComplete="current-password" required value={password} disabled={busy || stillLocked} onChange={(e) => setPassword(e.target.value)} autoFocus={email !== ""} />
                          <button type="button" className="linkbtn small" onClick={() => setShow((v) => !v)} aria-pressed={show}>{show ? "Hide" : "Show"}</button>
                        </div>
                      </Field>
                      <Checkbox checked={remember} onChange={setRemember} disabled={busy}>Remember this device</Checkbox>
                      <button className="btn primary full" type="submit" disabled={busy || stillLocked || retryAfter !== null}>{busy ? "Signing in…" : "Sign in"}</button>
                      <div className="centre"><Link className="small" to="/password-reset">Forgotten password?</Link></div>
                    </>
                  )}
                </form>
              </div>
              <div className="panel">
                <div className="panel-body" style={{ gap: 8 }}>
                  <div className="row c">
                    <div className="stack gap-4 grow">
                      <div className="strong">Install Vision X on this device</div>
                      <div className="help">Runs in its own window and shows cached data when the connection drops. This browser: {installed ? "installed" : canInstall ? "not installed" : "install not offered"}.</div>
                    </div>
                    {!installed && <button className="btn" type="button" disabled={!canInstall} onClick={() => void install()} title={canInstall ? undefined : "This browser has not offered installation yet"}>Install</button>}
                  </div>
                </div>
              </div>
              <div className="small faint centre">Shared front-desk device? Lock the screen when you step away.</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function OrganisationChoice({ organisations, busy, onChoose, onBack }: { organisations: OrgChoice[]; busy: boolean; onChoose: (orgId: string) => void; onBack: () => void }) {
  return (
    <div className="stack" role="group" aria-label="Choose an organisation">
      <Alert tone="info"><b>Your email belongs to more than one practice.</b> Choose the one to sign in to.</Alert>
      <div className="list panel" style={{ padding: 0 }}>
        {organisations.map((o) => (
          <div key={o.orgId} className="item">
            <div className="grow"><div className="primary">{o.tradingName ?? o.legalName}</div>{o.tradingName && <div className="sub">{o.legalName}</div>}</div>
            <button className="btn sm primary" type="button" disabled={busy} onClick={() => onChoose(o.orgId)}>Sign in</button>
          </div>
        ))}
      </div>
      <button className="btn ghost" type="button" onClick={onBack}><Icon name="chevron" style={{ transform: "rotate(90deg)" }} />Back</button>
    </div>
  );
}
